import {
  Card,
  CardActions,
  CardHeader,
  CardMedia,
  IconButton,
  Stack,
  useTheme,
} from '@mui/material';
import { forwardRef } from 'react';

import { tPortalLib } from '../../locales';
import { PortalTheme } from '../../theme';
import { AppAdaptedTooltip } from '../app-adapted-tooltip.component';
import { QrIcon } from '../icons';
import { AccountInfo } from './account-info.component';

export interface AccountCardProps {
  id: string;
  type: 'CREDIT' | 'PREPAID' | 'DIRECT';
  balance: number | undefined;
  maxCreditLimit: number | undefined;
  displayName: string;
  costCenterDisplayName?: string;

  toggleAccountDetails?: () => void;
  hidePrice: boolean | undefined;
  showQrCode: boolean | undefined;

  localeKey: string;
  currencyFormatter: (value: number, hideZeroDecimals?: boolean) => string;
  showBalance: boolean;
  toggleBalance: () => void;
}

export const AccountCard = forwardRef<HTMLDivElement, AccountCardProps>(
  (props, ref) => {
    const theme = useTheme<PortalTheme>();

    return (
      <Card
        ref={ref}
        sx={{
          width: '100%',
          minHeight: '100%',
          maxHeight: '200px',
          height: '200px',
          pointerEvents: 'all',
          cursor: 'pointer',
          background: theme.card.color,

          '&:focus-within': {
            /*
             * The card is not a focusable component but is still clickable.
             * At the moment it doesn't need to be since there is a button
             * that perform the same action as clicking the card. But when
             * focusing a button in the card it should be more clear that we
             * have focused something in the card.
             *
             * An alternative solution would be to use a standard html button
             * instead and style it same as current card. But that would
             * circumvent theming which is needed in the future.
             */
            background: theme.card.color,
          },
        }}
        onClick={props.toggleAccountDetails}
      >
        <Stack
          height="100%"
          justifyContent={'space-between'}
          flexDirection={'column'}
        >
          {theme.card.style !== 'none' &&
            !theme.card.style.startsWith('animation') && (
              <CardMedia
                image={`assets/card-shine-${theme.card.style}.png`}
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  height: '100%',
                  width: '100%',
                  borderRadius: `${theme.borderRadiusLarge}px`,
                  opacity: '1.0',
                }}
              />
            )}
          <CardHeader
            title={props.displayName}
            subheader={props.costCenterDisplayName}
            slotProps={{
              title: {
                variant: 'h4',
                sx: {
                  color: theme.card.contrastText,
                  zIndex: 1,
                },
              },
              subheader: {
                variant: 'body2',
                sx: {
                  color: theme.card.secondaryContrastText,
                  zIndex: 1,
                },
              },
            }}
            sx={{
              alignItems: 'start',
              p: 3,
              pb: 0,
              zIndex: 1,
            }}
          />

          <CardActions
            disableSpacing
            sx={{
              justifyContent: 'space-between',
              p: 3,
              pt: 0,
              alignItems: 'end',
              zIndex: 1,
            }}
          >
            {props.hidePrice ? (
              <Stack mt="auto" justifyContent="end" />
            ) : (
              <AccountInfo
                id={props.id}
                type={props.type}
                balance={props.balance}
                maxCreditLimit={props.maxCreditLimit}
                currencyFormatter={props.currencyFormatter}
                localeKey={props.localeKey}
                showBalance={props.showBalance}
                toggleBalance={props.toggleBalance}
              />
            )}

            {props.showQrCode && (
              <AppAdaptedTooltip
                title={tPortalLib.accountCard.showQrCode()}
                id={`qr-tooltip-${props.id}`}
              >
                <IconButton
                  aria-describedby={`qr-tooltip-${props.id}`}
                  size="large"
                  sx={{
                    color: theme.card.contrastText,
                    p: 0,
                  }}
                  aria-description={tPortalLib.accountCard.showQrCode()}
                  onClick={(e) => {
                    e.stopPropagation();
                    props.toggleAccountDetails?.();
                  }}
                >
                  <QrIcon />
                </IconButton>
              </AppAdaptedTooltip>
            )}
          </CardActions>
        </Stack>
      </Card>
    );
  },
);
